import { create } from "zustand";

export type TranscriptionStatus =
  | "transcribing"
  | "pause"
  | "polling"
  | "inactive";
export type VadStatus = "detected" | "undetected";

// 録音中断要求の理由。
// - track_ended: 録音中の MediaStreamTrack が終了した(終了状態から復帰することは
//   ないため、デバイス一覧による再確認なしで中断してよい確定的な証拠)
// - device_changed: 解決されたマイクデバイスが切り替わった(一覧の揺らぎや既定
//   デバイスの変更でも起きるため、切断の再確認を経てから中断する)
export type AudioInputInterruptionReason = "track_ended" | "device_changed";

// 中断要求を出した時点で録音に使われていたデバイス。切断の確認はこのスナップ
// ショットに対して行う。要求元(autoset)は選択デバイスをフォールバック先へ
// 更新してから要求を出すため、確認時点の選択 ID を見ると「切断したデバイス」
// ではなく「生きているフォールバック先」を確認してしまい、必ず接続ありと
// 判定されてしまう。
export type AudioInputDeviceSnapshot = {
  deviceId: string | null;
  deviceName: string | null;
  defaultDeviceLabel: string | null;
};

// 未消費の中断要求。理由とデバイスのスナップショットを要求本体に載せて運ぶ
// ことで、要求と内容の対応が上書きで崩れないようにする。消費前に複数の要求が
// 来た場合、reasons は蓄積され(track_ended が後着の device_changed に
// 打ち消されない)、スナップショットは最初の要求のものを保持する(切断が
// 起きる前の状態を確認対象にする)。
export type AudioInputInterruptionRequest = {
  seq: number;
  reasons: AudioInputInterruptionReason[];
  deviceSnapshot: AudioInputDeviceSnapshot;
};

type UseGlobalTranscriptionStatus = {
  status: TranscriptionStatus;
  vadStatus: VadStatus;
  vadStatusOccurredAt: string | null;
  isRequesting: boolean;
  canStart: boolean;
  isTranscriberActive: boolean;
  cancelRecordingRequestCount: number;
  clearEditorRequestCount: number;
  audioInputInterruptionRequest: AudioInputInterruptionRequest | null;
  setStatus: (status: TranscriptionStatus) => void;
  setVadStatus: (status: VadStatus, occurredAt: string) => void;
  resetVadStatus: () => void;
  setIsRequesting: (isRequesting: boolean) => void;
  setCanStart: (canStart: boolean) => void;
  setIsTranscriberActive: (isTranscriberActive: boolean) => void;
  requestCancelRecording: () => void;
  requestClearEditor: () => void;
  requestAudioInputInterruption: (
    reason: AudioInputInterruptionReason,
    deviceSnapshot: AudioInputDeviceSnapshot,
  ) => void;
  consumeAudioInputInterruption: (
    handledRequest: AudioInputInterruptionRequest,
  ) => void;
};

export const useGlobalTranscriptionStatus =
  create<UseGlobalTranscriptionStatus>()((set) => ({
    status: "inactive",
    vadStatus: "undetected",
    vadStatusOccurredAt: null,
    isRequesting: false,
    canStart: true,
    isTranscriberActive: false,
    cancelRecordingRequestCount: 0,
    clearEditorRequestCount: 0,
    audioInputInterruptionRequest: null,
    setStatus: (status) => set({ status }),
    setVadStatus: (vadStatus, vadStatusOccurredAt) =>
      set((state) => {
        const nextOccurredAt = Date.parse(vadStatusOccurredAt);
        if (Number.isNaN(nextOccurredAt)) return state;
        const currentOccurredAt = state.vadStatusOccurredAt
          ? Date.parse(state.vadStatusOccurredAt)
          : null;
        if (
          currentOccurredAt !== null &&
          !Number.isNaN(currentOccurredAt) &&
          nextOccurredAt < currentOccurredAt
        ) {
          return state;
        }
        return { vadStatus, vadStatusOccurredAt };
      }),
    resetVadStatus: () =>
      set({
        vadStatus: "undetected",
        vadStatusOccurredAt: null,
      }),
    setIsRequesting: (isRequesting) => set({ isRequesting }),
    setCanStart: (canStart) => set({ canStart }),
    setIsTranscriberActive: (isTranscriberActive) =>
      set({ isTranscriberActive }),
    requestCancelRecording: () =>
      set((state) => ({
        cancelRecordingRequestCount: state.cancelRecordingRequestCount + 1,
      })),
    requestClearEditor: () =>
      set((state) => ({
        clearEditorRequestCount: state.clearEditorRequestCount + 1,
      })),
    requestAudioInputInterruption: (reason, deviceSnapshot) =>
      set((state) => {
        const previous = state.audioInputInterruptionRequest;
        return {
          audioInputInterruptionRequest: {
            seq: (previous?.seq ?? 0) + 1,
            reasons: previous
              ? Array.from(new Set([...previous.reasons, reason]))
              : [reason],
            // 未消費の要求がある場合、最初の要求のスナップショット(切断前の
            // 状態)を確認対象として保持する。
            deviceSnapshot: previous?.deviceSnapshot ?? deviceSnapshot,
          },
        };
      }),
    // 処理した要求と現在の要求が同一の場合のみ消費する(compare-and-swap)。
    // 無条件に null にすると、レンダーから effect 実行までの間に届いた新しい
    // 要求(確定証拠の track_ended を含みうる)まで消してしまう。
    consumeAudioInputInterruption: (handledRequest) =>
      set((state) =>
        state.audioInputInterruptionRequest === handledRequest
          ? { audioInputInterruptionRequest: null }
          : {},
      ),
  }));
